/**
 * AZKA theme enhancements — intentionally minimal.
 * 1. Section reveal on scroll (respects prefers-reduced-motion).
 * 2. Superscripts the ® mark in dynamic text (product titles etc.) once per load.
 */
(function () {
  'use strict';

  /* 1. Reveal on scroll ---------------------------------------------------- */
  function initReveal() {
    var nodes = document.querySelectorAll('.azka-reveal');
    if (!nodes.length) return;

    // In the Theme Editor, never keep content hidden waiting on scroll — it
    // makes editing confusing. Show everything immediately.
    var inDesignMode = window.Shopify && window.Shopify.designMode;

    if (
      inDesignMode ||
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      nodes.forEach(function (n) {
        n.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    nodes.forEach(function (n) {
      observer.observe(n);
    });
  }

  /* 2. ® superscript (single pass) ----------------------------------------- */
  function superscriptRmark(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.indexOf('\u00AE') === -1) {
          return NodeFilter.FILTER_REJECT;
        }
        var parent = node.parentNode;
        if (!parent) return NodeFilter.FILTER_REJECT;
        var tag = parent.nodeName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA' || tag === 'SUP') {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    var nodes = [];
    var current;
    while ((current = walker.nextNode())) nodes.push(current);

    nodes.forEach(function (node) {
      var parts = node.nodeValue.split('\u00AE');
      if (parts.length < 2) return;
      var fragment = document.createDocumentFragment();
      parts.forEach(function (part, index) {
        if (index > 0) {
          var sup = document.createElement('sup');
          sup.className = 'rmark';
          sup.textContent = '\u00AE';
          fragment.appendChild(sup);
        }
        if (part) fragment.appendChild(document.createTextNode(part));
      });
      node.parentNode.replaceChild(fragment, node);
    });
  }

  /* 3. Form field sanitization --------------------------------------------- */
  function sanitizeTelInput(input) {
    var value = input.value;
    var leadingPlus = value.charAt(0) === '+';
    value = value.replace(/[^\d\s\-().+]/g, '');
    if (leadingPlus) {
      value = '+' + value.replace(/\+/g, '');
    } else {
      value = value.replace(/\+/g, '');
    }
    if (value.length > 24) value = value.slice(0, 24);
    if (input.value !== value) input.value = value;
  }

  function sanitizeEmailInput(input) {
    var value = input.value.replace(/\s/g, '');
    if (input.value !== value) input.value = value;
  }

  function initFormValidation(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.azka-form').forEach(function (form) {
      if (form.dataset.azkaValidation === 'true') return;
      form.dataset.azkaValidation = 'true';

      form.querySelectorAll('input[type="tel"]').forEach(function (input) {
        input.addEventListener('input', function () {
          sanitizeTelInput(input);
        });
      });

      form.querySelectorAll('input[type="email"]').forEach(function (input) {
        input.addEventListener('input', function () {
          sanitizeEmailInput(input);
        });
      });
    });
  }

  /* 4. Count-up for genuine statistics ------------------------------------- */
  function initCountUp(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-azka-countup]:not([data-countup-done])');
    if (!nodes.length) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function parse(text) {
      // Split into prefix, numeric core, suffix — e.g. "40%+" -> "", "40", "%+"
      var match = String(text).match(/^(\D*)([\d][\d.,]*)(.*)$/);
      if (!match) return null;
      var numeric = match[2].replace(/[.,]/g, '');
      var value = parseInt(numeric, 10);
      if (isNaN(value)) return null;
      return { prefix: match[1], value: value, suffix: match[3] };
    }

    function animate(node) {
      var parsed = parse(node.textContent);
      node.setAttribute('data-countup-done', '');
      // No genuine number, no motion support, or reduced motion: leave final value as-is.
      if (!parsed || reduceMotion || !('requestAnimationFrame' in window)) return;

      var duration = 1100;
      var start = null;
      var final = parsed.value;

      function step(timestamp) {
        if (start === null) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        // easeOutCubic
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = Math.round(eased * final);
        node.textContent = parsed.prefix + current + parsed.suffix;
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          node.textContent = parsed.prefix + final + parsed.suffix;
        }
      }
      window.requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(animate);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animate(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.4 }
    );

    nodes.forEach(function (n) {
      observer.observe(n);
    });
  }

  function init() {
    initReveal();
    initFormValidation(document);
    initCountUp(document);
    try {
      superscriptRmark(document.body);
    } catch (e) {
      /* non-critical enhancement */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initReveal();
    initFormValidation(event.target);
    initCountUp(event.target);
    try {
      superscriptRmark(event.target);
    } catch (e) {
      /* non-critical enhancement */
    }
  });
})();
